import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Loader from "../../components/Loader/Loader";
import Pagination from "../../components/Pagination/Pagination";
import ShiftEmployeePicker from "../../components/Shifts/ShiftEmployeePicker";
import ShiftScheduleDetail from "../../components/Shifts/ShiftScheduleDetail";
import { getAuthHeaders } from "../../utils/auth";
import {
  DEFAULT_WEEKDAY_CODES,
  SHIFT_DAY_OPTIONS,
  buildDaySchedulesPayload,
  cloneDaySchedule,
  createDefaultDaySchedule,
  expandShiftToDayScheduleMap,
  formatDaysSummary,
  formatShiftTimesSummary,
  sortDayCodes,
  suggestPolicyDefaults,
} from "../../utils/shiftSchedule";

const defaultSchedulesForDays = (days) => {
  const map = {};
  sortDayCodes(days).forEach((day) => {
    map[day] = createDefaultDaySchedule();
  });
  return map;
};

const EMPLOYEE_ASSIGN_BATCH_SIZE = 150;
const EMPLOYEE_ASSIGN_BATCH_TIMEOUT_MS = 120000;

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const ShiftsManagement = ({ userProfile }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const statusOptions = ["All", "Active", "Inactive"];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [viewingShift, setViewingShift] = useState(null);
  const [shiftName, setShiftName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isSpecialShift, setIsSpecialShift] = useState(false);
  const [selectedDays, setSelectedDays] = useState(() => [...DEFAULT_WEEKDAY_CODES]);
  const [daySchedulesByDay, setDaySchedulesByDay] = useState(() =>
    defaultSchedulesForDays(DEFAULT_WEEKDAY_CODES)
  );
  const [expandedDay, setExpandedDay] = useState(DEFAULT_WEEKDAY_CODES[0]);
  const [daysError, setDaysError] = useState("");
  const [copyMenuSourceDay, setCopyMenuSourceDay] = useState(null);
  const [copyTargetDays, setCopyTargetDays] = useState([]);
  const copyMenuRef = useRef(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [initialAssignedIds, setInitialAssignedIds] = useState([]);
  const [assignProgress, setAssignProgress] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isSuper = userProfile?.role === "super";

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchShifts = async (page = 1) => {
    if (!isSuper) return;
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const params = {
        page,
        page_size: pageSize,
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
      };
      if (statusFilter === "Active") params.active = true;
      if (statusFilter === "Inactive") params.active = false;

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/shifts`,
        { headers, params }
      );
      setShifts(response.data.shifts || []);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error("Error fetching shifts:", error);
      alert(error.response?.data?.detail || "Failed to fetch shifts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuper) {
      fetchShifts(currentPage);
    } else {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, statusFilter, isSuper]);

  const resetForm = () => {
    setShiftName("");
    setIsActive(true);
    setIsSpecialShift(false);
    setSelectedDays([...DEFAULT_WEEKDAY_CODES]);
    setDaySchedulesByDay(defaultSchedulesForDays(DEFAULT_WEEKDAY_CODES));
    setExpandedDay(DEFAULT_WEEKDAY_CODES[0]);
    setDaysError("");
    setCopyMenuSourceDay(null);
    setCopyTargetDays([]);
    setEditingShift(null);
    setSelectedEmployeeIds([]);
    setInitialAssignedIds([]);
    setAssignProgress(null);
  };

  const loadAssignedEmployees = async (shiftId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${shiftId}/assigned-employee-ids`,
        { headers }
      );
      const ids = response.data.employee_ids || [];
      setSelectedEmployeeIds(ids);
      setInitialAssignedIds(ids);
    } catch (error) {
      console.error("Error loading assigned employees:", error);
      setSelectedEmployeeIds([]);
      setInitialAssignedIds([]);
    }
  };

  const openCreateModal = () => {
    setViewingShift(null);
    resetForm();
    setInitialAssignedIds([]);
    setIsModalOpen(true);
  };

  const openEditModal = async (shift) => {
    setViewingShift(null);
    setEditingShift(shift);
    setShiftName(shift.name || "");
    setIsActive(shift.is_active !== false);
    setIsSpecialShift(shift.is_special_shift === true);
    const days = shift.days?.length
      ? sortDayCodes(shift.days)
      : [...DEFAULT_WEEKDAY_CODES];
    setSelectedDays(days);
    const map = expandShiftToDayScheduleMap(shift);
    days.forEach((day) => {
      if (!map[day]) map[day] = createDefaultDaySchedule();
    });
    setDaySchedulesByDay(map);
    setExpandedDay(days[0] || null);
    setDaysError("");
    setIsModalOpen(true);
    if (shift.id) {
      await loadAssignedEmployees(shift.id);
    }
  };

  const handleEditFromDetail = (shift) => {
    setViewingShift(null);
    openEditModal(shift);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setIsModalOpen(false);
    resetForm();
  };

  const toggleDay = (code) => {
    setSelectedDays((prev) => {
      const next = prev.includes(code)
        ? prev.filter((d) => d !== code)
        : [...prev, code];
      const sorted = sortDayCodes(next);
      setDaySchedulesByDay((schedules) => {
        const updated = { ...schedules };
        if (!prev.includes(code)) {
          const templateDay = prev[0];
          updated[code] = cloneDaySchedule(
            templateDay ? schedules[templateDay] : createDefaultDaySchedule()
          );
        }
        return updated;
      });
      setExpandedDay((current) => {
        if (sorted.includes(current)) return current;
        return sorted[0] || null;
      });
      return sorted;
    });
    setDaysError("");
  };

  const setDaysPreset = (codes) => {
    const sorted = sortDayCodes(codes);
    setSelectedDays(sorted);
    setDaySchedulesByDay((schedules) => {
      const updated = { ...schedules };
      const templateDay = sorted.find((d) => schedules[d]) || Object.keys(schedules)[0];
      const template = cloneDaySchedule(
        templateDay ? schedules[templateDay] : createDefaultDaySchedule()
      );
      sorted.forEach((day) => {
        if (!updated[day]) updated[day] = cloneDaySchedule(template);
      });
      return updated;
    });
    setExpandedDay(sorted[0] || null);
    setDaysError("");
  };

  const updateDaySchedule = (day, patch) => {
    setDaySchedulesByDay((prev) => ({
      ...prev,
      [day]: { ...(prev[day] || createDefaultDaySchedule()), ...patch },
    }));
  };

  const setDayField = (day, field, value) => {
    updateDaySchedule(day, { [field]: value });
  };

  const handleDayFullDayChange = (day, checked) => {
    const current = daySchedulesByDay[day] || createDefaultDaySchedule();
    if (checked) {
      const suggested = suggestPolicyDefaults(
        "00:00",
        "24:00",
        current.break_duration_minutes
      );
      updateDaySchedule(day, {
        is_full_day: true,
        start_time: "00:00",
        end_time: "24:00",
        ...suggested,
        check_out_end_24: suggested.check_out_end === "24:00",
      });
    } else {
      updateDaySchedule(day, {
        is_full_day: false,
        start_time: current.start_time === "00:00" ? "08:00" : current.start_time,
        end_time: current.end_time === "24:00" ? "17:00" : current.end_time,
      });
    }
  };

  const applySuggestedPolicyForDay = (day) => {
    const current = daySchedulesByDay[day] || createDefaultDaySchedule();
    const start_time = current.is_full_day ? "00:00" : current.start_time;
    const end_time = current.is_full_day ? "24:00" : current.end_time;
    const suggested = suggestPolicyDefaults(
      start_time,
      end_time,
      current.break_duration_minutes
    );
    updateDaySchedule(day, {
      ...suggested,
      check_out_end_24: suggested.check_out_end === "24:00",
    });
  };

  const closeCopyMenu = useCallback(() => {
    setCopyMenuSourceDay(null);
    setCopyTargetDays([]);
  }, []);

  useEffect(() => {
    closeCopyMenu();
  }, [selectedDays, closeCopyMenu]);

  useEffect(() => {
    if (copyMenuSourceDay && expandedDay !== copyMenuSourceDay) {
      closeCopyMenu();
    }
  }, [expandedDay, copyMenuSourceDay, closeCopyMenu]);

  useEffect(() => {
    if (!copyMenuSourceDay) return undefined;

    const handlePointerDown = (event) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(event.target)) {
        closeCopyMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeCopyMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [copyMenuSourceDay, closeCopyMenu]);

  const copyDayToSelectedTargets = (sourceDay, targetDays) => {
    const source = daySchedulesByDay[sourceDay];
    if (!source || !targetDays?.length) return;
    const cloned = cloneDaySchedule(source);
    setDaySchedulesByDay((prev) => {
      const updated = { ...prev };
      targetDays.forEach((day) => {
        if (day !== sourceDay) {
          updated[day] = cloneDaySchedule(cloned);
        }
      });
      return updated;
    });
  };

  const openCopyMenu = (sourceDay) => {
    if (copyMenuSourceDay === sourceDay) {
      closeCopyMenu();
      return;
    }
    setCopyMenuSourceDay(sourceDay);
    setCopyTargetDays([]);
  };

  const toggleCopyTargetDay = (dayCode) => {
    setCopyTargetDays((prev) =>
      prev.includes(dayCode)
        ? prev.filter((code) => code !== dayCode)
        : [...prev, dayCode]
    );
  };

  const applyCopyToSelectedTargets = (sourceDay) => {
    if (!copyTargetDays.length) return;
    copyDayToSelectedTargets(sourceDay, copyTargetDays);
    closeCopyMenu();
  };

  const handleSave = async () => {
    const trimmedName = shiftName.trim();
    if (trimmedName.length < 2) {
      alert("Shift name must be at least 2 characters.");
      return;
    }
    if (!selectedDays.length) {
      setDaysError("Select at least one working day.");
      return;
    }

    for (const day of selectedDays) {
      const s = daySchedulesByDay[day];
      if (!s) continue;
      if (!s.is_full_day && (!s.start_time || !s.end_time)) {
        alert(`Start and end times are required for ${day}.`);
        setExpandedDay(day);
        return;
      }
    }

    const day_schedules = buildDaySchedulesPayload(selectedDays, daySchedulesByDay);

    setSaving(true);
    setAssignProgress(null);
    try {
      const headers = await getAuthHeaders();
      const body = {
        name: trimmedName,
        day_schedules,
        days: sortDayCodes(selectedDays),
        is_active: isActive,
        is_special_shift: isSpecialShift,
      };

      let shiftId = editingShift?.id;
      let savedShift = editingShift;

      if (shiftId) {
        const response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${shiftId}`,
          body,
          { headers }
        );
        savedShift = response.data;
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/shifts`,
          body,
          { headers }
        );
        shiftId = response.data.id;
        savedShift = response.data;
        setEditingShift(response.data);
      }

      const baselineSet = new Set(initialAssignedIds);
      const selectedSet = new Set(selectedEmployeeIds);
      const toUnassign = initialAssignedIds.filter((id) => !selectedSet.has(id));
      const toAssign = selectedEmployeeIds.filter((id) => !baselineSet.has(id));

      const unassignBatches = chunkArray(toUnassign, EMPLOYEE_ASSIGN_BATCH_SIZE);
      const assignBatches = chunkArray(toAssign, EMPLOYEE_ASSIGN_BATCH_SIZE);
      const totalBatches = unassignBatches.length + assignBatches.length;
      let completedBatches = 0;

      try {
        if (totalBatches > 0) {
          setAssignProgress({ completed: 0, total: totalBatches });
        }

        for (const chunk of unassignBatches) {
          await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${shiftId}/employees`,
            { employee_ids: chunk, mode: "unassign" },
            { headers, timeout: EMPLOYEE_ASSIGN_BATCH_TIMEOUT_MS }
          );
          completedBatches += 1;
          setAssignProgress({ completed: completedBatches, total: totalBatches });
        }

        for (const chunk of assignBatches) {
          await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${shiftId}/employees`,
            { employee_ids: chunk, mode: "assign" },
            { headers, timeout: EMPLOYEE_ASSIGN_BATCH_TIMEOUT_MS }
          );
          completedBatches += 1;
          setAssignProgress({ completed: completedBatches, total: totalBatches });
        }

        setInitialAssignedIds([...selectedEmployeeIds]);
        await fetchShifts(currentPage);
        closeModal(true);
      } catch (assignError) {
        console.error("Error assigning employees to shift:", assignError);
        setEditingShift(savedShift);
        // Refresh baseline from server so retry only sends remaining diffs
        try {
          const refresh = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${shiftId}/assigned-employee-ids`,
            { headers }
          );
          const latestIds = refresh.data.employee_ids || [];
          setInitialAssignedIds(latestIds);
        } catch (refreshError) {
          console.error("Error refreshing assigned employees after failure:", refreshError);
        }
        const detail = assignError.response?.data?.detail;
        const timedOut =
          assignError.code === "ECONNABORTED" ||
          assignError.message?.toLowerCase?.().includes("timeout");
        const progressNote =
          totalBatches > 0
            ? ` (${completedBatches}/${totalBatches} batches done)`
            : "";
        alert(
          detail ||
            (timedOut
              ? `Shift saved but assigning employees timed out${progressNote}. Please try Save again.`
              : `Shift saved but failed to assign employees${progressNote}. Please try Save again.`)
        );
      }
    } catch (error) {
      console.error("Error saving shift:", error);
      alert(error.response?.data?.detail || "Failed to save shift.");
    } finally {
      setSaving(false);
      setAssignProgress(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${deleteTarget.id}`,
        { headers }
      );
      if (viewingShift?.id === deleteTarget.id) {
        setViewingShift(null);
      }
      setDeleteTarget(null);
      await fetchShifts(currentPage);
    } catch (error) {
      console.error("Error deleting shift:", error);
      alert(error.response?.data?.detail || "Failed to delete shift.");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = (status) => {
    setCurrentPage(1);
    setStatusFilter(status);
  };

  const formatTimeDisplay = (shift) => formatShiftTimesSummary(shift);

  const ShiftIcon = () => (
    <svg
      width="1.5em"
      height="1.5em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );

  const renderDayScheduleEditor = (day) => {
    const schedule = daySchedulesByDay[day] || createDefaultDaySchedule();
    const dayLabel =
      SHIFT_DAY_OPTIONS.find((d) => d.code === day)?.label || day;

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-left text-sm font-semibold text-gray-800"
          onClick={() =>
            setExpandedDay((current) => (current === day ? null : day))
          }
        >
          <span>
            {dayLabel}{" "}
            <span className="font-normal text-gray-500">
              {schedule.is_full_day
                ? "00:00–24:00"
                : `${schedule.start_time}–${schedule.end_time}`}
            </span>
          </span>
          <span className="text-xs text-gray-500">
            {expandedDay === day ? "Hide" : "Edit"}
          </span>
        </button>
        {expandedDay === day && (
          <div className="p-3 space-y-3 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!schedule.is_full_day}
                  onChange={(e) =>
                    handleDayFullDayChange(day, e.target.checked)
                  }
                />
                Full day (00:00 – 24:00)
              </label>
              {selectedDays.length > 1 && (
                <div className="relative" ref={copyMenuSourceDay === day ? copyMenuRef : undefined}>
                  <button
                    type="button"
                    onClick={() => openCopyMenu(day)}
                    className="text-xs text-[#674936] underline"
                    aria-haspopup="menu"
                    aria-expanded={copyMenuSourceDay === day}
                  >
                    Copy to other days
                  </button>
                  {copyMenuSourceDay === day && (
                    <div
                      role="menu"
                      className="absolute left-0 top-full z-30 mt-1 min-w-[14rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                    >
                      <p className="px-3 py-1.5 text-xs font-medium text-gray-500">
                        Copy schedule to
                      </p>
                      <div className="max-h-56 overflow-y-auto">
                        {selectedDays
                          .filter((code) => code !== day)
                          .map((code) => {
                            const label =
                              SHIFT_DAY_OPTIONS.find((d) => d.code === code)?.label ||
                              code;
                            const selected = copyTargetDays.includes(code);
                            return (
                              <label
                                key={code}
                                role="menuitemcheckbox"
                                aria-checked={selected}
                                className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 ${
                                  selected ? "bg-gray-50" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleCopyTargetDay(code)}
                                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#674936] focus:ring-[#674936]"
                                />
                                <span>{label}</span>
                              </label>
                            );
                          })}
                      </div>
                      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="text-xs text-[#674936] underline"
                            onClick={() =>
                              setCopyTargetDays(
                                selectedDays.filter((code) => code !== day)
                              )
                            }
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="text-xs text-gray-500 underline"
                            onClick={() => setCopyTargetDays([])}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="text-xs text-gray-600"
                            onClick={closeCopyMenu}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={copyTargetDays.length === 0}
                            onClick={() => applyCopyToSelectedTargets(day)}
                            className="rounded bg-[#674936] px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => applySuggestedPolicyForDay(day)}
                className="text-xs text-[#674936] underline"
              >
                Suggest policy from schedule
              </button>
            </div>
            {!schedule.is_full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={schedule.start_time}
                    onChange={(e) =>
                      setDayField(day, "start_time", e.target.value)
                    }
                    className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={
                      schedule.end_time === "24:00"
                        ? "23:59"
                        : schedule.end_time
                    }
                    onChange={(e) =>
                      setDayField(day, "end_time", e.target.value)
                    }
                    className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Late allowable (min)
                </label>
                <input
                  type="number"
                  min={0}
                  value={schedule.late_allowed_minutes}
                  onChange={(e) =>
                    setDayField(day, "late_allowed_minutes", e.target.value)
                  }
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Early leave allowable (min)
                </label>
                <input
                  type="number"
                  min={0}
                  value={schedule.early_leave_allowed_minutes}
                  onChange={(e) =>
                    setDayField(
                      day,
                      "early_leave_allowed_minutes",
                      e.target.value
                    )
                  }
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Break duration (min)
                </label>
                <input
                  type="number"
                  min={0}
                  value={schedule.break_duration_minutes}
                  onChange={(e) =>
                    setDayField(day, "break_duration_minutes", e.target.value)
                  }
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Min. work time (min)
                </label>
                <input
                  type="number"
                  min={1}
                  value={schedule.min_work_minutes}
                  onChange={(e) =>
                    setDayField(day, "min_work_minutes", e.target.value)
                  }
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Valid check-in period
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="time"
                  value={schedule.check_in_start}
                  onChange={(e) =>
                    setDayField(day, "check_in_start", e.target.value)
                  }
                  className="flex-1 border rounded px-2 py-1.5 text-sm"
                />
                <span className="text-gray-500">–</span>
                <input
                  type="time"
                  value={schedule.check_in_end}
                  onChange={(e) =>
                    setDayField(day, "check_in_end", e.target.value)
                  }
                  className="flex-1 border rounded px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Valid check-out period
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="time"
                  value={schedule.check_out_start}
                  onChange={(e) =>
                    setDayField(day, "check_out_start", e.target.value)
                  }
                  className="flex-1 border rounded px-2 py-1.5 text-sm"
                />
                <span className="text-gray-500">–</span>
                {!schedule.check_out_end_24 ? (
                  <input
                    type="time"
                    value={
                      schedule.check_out_end === "24:00"
                        ? "23:59"
                        : schedule.check_out_end
                    }
                    onChange={(e) =>
                      setDayField(day, "check_out_end", e.target.value)
                    }
                    className="flex-1 border rounded px-2 py-1.5 text-sm"
                  />
                ) : (
                  <span className="flex-1 text-sm text-gray-600 py-1.5">
                    24:00
                  </span>
                )}
              </div>
              <label className="inline-flex items-center gap-2 text-xs mt-2">
                <input
                  type="checkbox"
                  checked={!!schedule.check_out_end_24}
                  onChange={(e) => {
                    updateDaySchedule(day, {
                      check_out_end_24: e.target.checked,
                      check_out_end: e.target.checked
                        ? "24:00"
                        : schedule.check_out_end === "24:00"
                          ? "23:28"
                          : schedule.check_out_end,
                    });
                  }}
                />
                Check-out end at 24:00
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isSuper) {
    return (
      <div className="p-4 sm:p-5 w-full max-w-6xl mx-auto">
        <p className="text-gray-600">You do not have permission to manage shifts.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 w-full min-w-0 max-w-6xl mx-auto">
      <div className="section-header !md:flex-row !flex-row !items-center bg-[#674936] text-white p-4 rounded">
        <h3 className="flex gap-4 text-white text-[1rem] md:text-[1.6rem] font-bold">
          <ShiftIcon /> Shift Management
        </h3>
      </div>

      <div className="my-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {statusOptions.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusChange(status)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                statusFilter === status
                  ? "bg-white text-[#674936] shadow"
                  : "bg-[#674936] text-white hover:bg-[#ebebeb] hover:shadow hover:text-[#674936]"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search shifts..."
            className="border rounded px-3 py-2 text-sm w-48 sm:w-64"
          />
          <button
            type="button"
            onClick={openCreateModal}
            className="px-4 py-2 rounded bg-[#674936] text-white text-sm hover:bg-[#503220]"
          >
            Add Shift
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Days</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center">
                  <Loader />
                </td>
              </tr>
            ) : shifts.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                  No shifts found.
                </td>
              </tr>
            ) : (
              shifts.map((shift) => (
                <tr key={shift.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {shift.name}
                    {shift.is_special_shift && (
                      <span className="ml-2 text-xs text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                        Special
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatDaysSummary(shift.days)}
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate" title={formatTimeDisplay(shift)}>
                    {formatTimeDisplay(shift)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        shift.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {shift.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setViewingShift(shift)}
                        className="text-[#674936] underline text-xs"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditModal(shift)}
                        className="text-[#674936] underline text-xs"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(shift)}
                        className="text-red-700 underline text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="p-4 flex justify-center">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        </div>
      )}

      {viewingShift && (
        <ShiftScheduleDetail
          shift={viewingShift}
          onClose={() => setViewingShift(null)}
          onEdit={handleEditFromDetail}
        />
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto py-4 px-2 sm:items-center">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-3xl mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editingShift ? "Edit Shift" : "Add Shift"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={shiftName}
                  onChange={(e) => setShiftName(e.target.value)}
                  className="mt-1 w-full border rounded px-3 py-2"
                  placeholder="e.g. Morning Shift"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Working days
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {SHIFT_DAY_OPTIONS.map(({ code, label }) => {
                    const selected = selectedDays.includes(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => toggleDay(code)}
                        className={`px-2.5 py-1 rounded text-xs font-semibold border transition ${
                          selected
                            ? "bg-[#674936] text-white border-[#674936]"
                            : "bg-white text-gray-600 border-gray-300 hover:border-[#674936]"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setDaysPreset(DEFAULT_WEEKDAY_CODES)}
                    className="text-[#674936] underline"
                  >
                    Weekdays
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDaysPreset(SHIFT_DAY_OPTIONS.map((d) => d.code))
                    }
                    className="text-[#674936] underline"
                  >
                    All days
                  </button>
                  <button
                    type="button"
                    onClick={() => setDaysPreset([])}
                    className="text-gray-500 underline"
                  >
                    Clear
                  </button>
                </div>
                {daysError && (
                  <p className="text-red-600 text-xs mt-1">{daysError}</p>
                )}
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-800">
                  Per-day schedule &amp; policy
                </h4>
                {selectedDays.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Select at least one working day to configure times.
                  </p>
                ) : (
                  sortDayCodes(selectedDays).map((day) => (
                    <div key={day}>{renderDayScheduleEditor(day)}</div>
                  ))
                )}
              </div>

              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isSpecialShift}
                  onChange={(e) => setIsSpecialShift(e.target.checked)}
                />
                Special shift
              </label>

              <label className="inline-flex items-center gap-2 text-sm ml-10">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Active
              </label>

              <ShiftEmployeePicker
                selectedEmployeeIds={selectedEmployeeIds}
                onSelectionChange={setSelectedEmployeeIds}
                shiftId={editingShift?.id || null}
                disabled={saving}
              />

              {assignProgress && assignProgress.total > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Assigning employees…</span>
                    <span>
                      {assignProgress.completed}/{assignProgress.total}
                    </span>
                  </div>
                  <div className="h-0.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-[#674936] transition-[width] duration-300 ease-out"
                      style={{
                        width: `${Math.min(
                          100,
                          (assignProgress.completed / assignProgress.total) * 100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 rounded bg-[#674936] text-white hover:bg-[#503220]"
                disabled={saving}
              >
                {saving
                  ? assignProgress
                    ? `Assigning… ${assignProgress.completed}/${assignProgress.total}`
                    : "Saving..."
                  : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Shift</h3>
            <p className="text-sm text-gray-600 mb-4">
              Delete &quot;{deleteTarget.name}&quot; ({formatTimeDisplay(deleteTarget)})?
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 rounded bg-red-700 text-white hover:bg-red-800"
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftsManagement;
